import icon from '../../assets/icon.png'
import dashboard from '../../assets/dashboard.png'
import logout from '../../assets/logout.png'
import { useAuth } from '../../contexts/AuthContext';
import { generateInitialAvatar } from '../../lib/utils';
import LoadingSpinner from '../LoadingSpinner';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export function Sidebar({ isSuperAdmin = false }: { isSuperAdmin?: boolean }) {
    const [isOpen, setIsOpen] = useState(false);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const { signOut, signingOut, profile, user, loading } = useAuth();
    const navigate = useNavigate();
    const waitingForProfile = !user && loading;
    const location = useLocation();
    const isActive = (path: string) => location.pathname.startsWith(path);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleLogout = async () => {
        await signOut();
        navigate('/login');
    };

    if (isSuperAdmin) {
      return (
        <>
        {signingOut && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <LoadingSpinner size="lg" text="Logging out..." />
          </div>
        )}

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="fixed top-4 left-4 z-40 rounded-lg p-2 bg-[#0f1d2a] text-white md:hidden"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <aside
          ref={sidebarRef}
          className={`fixed inset-y-0 left-0 z-30 transform ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0 transition-transform duration-300 ease-in-out h-full w-[100px] flex flex-col items-center justify-between py-6 bg-[#0f1d2a] overflow-y-auto`}
          aria-label="Sidebar"
        >
          <img src={icon} alt="icon" className="w-[40px] h-[40px] mb-2" />
          <button
              onClick={() => {
                setIsOpen(false);
                handleLogout();
              }}
              className="flex flex-col items-center gap-1 text-white hover:opacity-80"
            >
              <div className="grid h-10 w-10 place-items-center rounded-md">
                <img src={logout} alt="icon" className="w-[45px] h-[45px]" />
              </div>
              <span className="text-xs mt-1">Logout</span>
          </button>
        </aside>
        </>
      );
    }

    if (waitingForProfile) {
      return (
        <>
          {signingOut && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <LoadingSpinner size="lg" text="Logging out..." />
            </div>
          )}
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <LoadingSpinner size="lg" text="Loading profile..." />
          </div>
        </>
      );
    }

    return (
      <>
        {signingOut && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <LoadingSpinner size="lg" text="Logging out..." />
          </div>
        )}

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="fixed top-4 left-4 z-40 rounded-lg p-2 bg-[#0f1d2a] text-white md:hidden"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Sidebar */}
        <aside
          ref={sidebarRef}
          className={`fixed inset-y-0 left-0 z-30 transform ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0 transition-transform duration-300 ease-in-out h-full w-[100px] flex flex-col items-center justify-between py-6 bg-[#0f1d2a] overflow-y-auto`}
          aria-label="Sidebar"
        >
          <div className="flex flex-col items-center gap-6">
            <img src={icon} alt="icon" className="w-[40px] h-[40px] mb-2" />
  
            {/* nav buttons */}
            <nav className="flex flex-col items-center gap-5 text-[11px]" aria-label="Primary">
              <Link 
                to="/dashboard" 
                className="flex flex-col items-center gap-1 text-white hover:opacity-80"
                onClick={() => setIsOpen(false)}
              >
                <div className={`grid h-12 w-12 place-items-center rounded-md ${isActive('/dashboard') ? 'bg-[#2bb6f7]' : 'bg-[#304b63]'}`}>
                  <img src={dashboard} alt="icon" className="w-[22px] h-[22px]" />
                </div>
                <span className="mt-2 text-xs">Dashboard</span>
              </Link>
            </nav>
          </div>
  
          <div className="flex flex-col items-center gap-3">
            <Link 
              to="/profile"
              onClick={() => setIsOpen(false)}
            >
              <img
                src={
                  (profile?.preSignedUrl && profile.preSignedUrl.trim() !== '')
                    ? profile.preSignedUrl
                    : (profile?.profilePic && profile.profilePic.trim() !== '')
                      ? profile.profilePic
                      : generateInitialAvatar(profile?.name || 'U', 96)
                }
                alt="Current user"
                className="h-12 w-12 rounded-full ring-2 mb-4 ring-white"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  target.src = generateInitialAvatar(profile?.name || 'U', 96);
                }}
              />
            </Link>
            <button
              onClick={() => {
                setIsOpen(false);
                handleLogout();
              }}
              className="flex flex-col items-center gap-1 text-white hover:opacity-80"
            >
              <div className="grid h-10 w-10 place-items-center rounded-md">
                <img src={logout} alt="icon" className="w-[45px] h-[45px]" />
              </div>
              <span className="text-xs mt-1">Logout</span>
            </button>
          </div>
        </aside>

        {/* Overlay */}
        {isOpen && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-20 md:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </>
    )
  }
  